import { ArrowTopRightOnSquareIcon } from "@heroicons/react/20/solid";
import { useTranslation } from "react-i18next";

import { ReturnDetail, ReturnOutcome } from "../../models/returns/IReturnsApi";
import classNames from "../../utils/classNames";
import formatDateTime from "../../utils/formatDateTime";
import { INBOX_DAY_TIME } from "../../utils/inboxDates";
import PriceFormat from "../../utils/priceFormat";
import { actionLabel } from "../../utils/returnsRules";

const OUTCOME_LABELS: Record<ReturnOutcome, string> = {
  HoldingBay: "In the holding bay",
  BackToStock: "Back to stock",
  QualityControl: "Quality control",
  Scrap: "Scrapped",
  PutAwayInYard: "Put away in Wilma Yard",
};

/**
 * When there's nothing to press: eBay has stepped in (red card), the return is closed (the summary),
 * or it's open but waiting on the buyer or eBay (one grey line).
 */
export default function ReadOnlyActions({ detail }: { detail: ReturnDetail }) {
  const { t } = useTranslation("returns");
  const when = (iso: string | null) => (iso ? formatDateTime(iso, INBOX_DAY_TIME) : "");
  const appeal = detail.appealUrl ?? detail.links.ebayReturnUrl;
  const closed = detail.stage === "Closed" || detail.ebayState === "CLOSED";
  // The API records our own "Asked eBay to step in" as a WilmaAction; anything else was the buyer or eBay.
  const escalatedByUs = detail.timeline.some((e) => e.kind === "WilmaAction" && !!e.body && e.body.startsWith("Asked eBay"));

  if (detail.escalated) {
    return (
      <div className="flex flex-col gap-2.5 px-4 pb-4 text-[12px]">
        <div className="flex flex-col gap-1 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-red-900">
          <span className="font-semibold">{t("eBay stepped in")}{detail.escalatedAt ? ` · ${when(detail.escalatedAt)}` : ""}</span>
          <span>{detail.readOnlyReason ?? t("eBay is deciding this return. Nothing can be changed here until it has.")}</span>
          {detail.actualRefund && <span>{t("eBay refunded the buyer {{amount}} and charged your account.", { amount: PriceFormat(detail.actualRefund.total) })}</span>}
          {!escalatedByUs && <span className="text-[11px]">{t("Counts against your seller performance.")}</span>}
        </div>
        <h3 className="text-[13px] font-semibold text-gray-500">{t("Options are read-only")}</h3>
        <div className="flex flex-wrap gap-1.5" aria-disabled="true">
          {(["accept", "refund_full", "message"] as const).map((kind) => (
            <button key={kind} type="button" disabled className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-[12px] text-gray-400">
              {actionLabel(kind)}
            </button>
          ))}
        </div>
        {appeal && (
          <a href={appeal} target="_blank" rel="noreferrer" className="inline-flex w-max items-center gap-1 font-semibold text-primary-700 hover:underline">
            {t("Appeal on eBay")} <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
          </a>
        )}
      </div>
    );
  }

  if (!closed) {
    return (
      <div className="flex flex-col gap-1 px-4 pb-4 text-[12px] text-gray-500">
        <span>{t("Nothing for you to do right now — waiting on the buyer or eBay.")}</span>
        {detail.readOnlyReason && <span className="text-[11px]">{detail.readOnlyReason}</span>}
      </div>
    );
  }

  const inspection = detail.inspection;
  const outbound = detail.order?.postage ?? 0;
  const cost = [
    detail.actualRefund ? t("{{amount}} refund", { amount: PriceFormat(detail.actualRefund.total) }) : null,
    outbound > 0 ? t("{{amount}} outbound", { amount: PriceFormat(outbound) }) : null,
    detail.label?.source === "ebay" ? t("eBay label") : null,
  ].filter(Boolean);

  return (
    <div className="flex flex-col gap-2 px-4 pb-4 text-[12px]">
      <dl className="grid grid-cols-[110px_minmax(0,1fr)] gap-x-2.5 gap-y-1.5">
        <dt className="text-gray-500">{t("Refunded")}</dt>
        <dd className="text-gray-900">
          {detail.actualRefund ? (
            <>
              <span className="font-semibold">
                {detail.estimatedRefund && detail.actualRefund.total >= detail.estimatedRefund.total ? t("{{amount}} in full", { amount: PriceFormat(detail.actualRefund.total) }) : PriceFormat(detail.actualRefund.total)}
              </span>
              {detail.stage === "Refunded" && detail.stageBy ? ` · ${detail.stageBy}` : ""}
              {detail.stage === "Refunded" && detail.stageAt ? ` · ${when(detail.stageAt)}` : ""}
            </>
          ) : (
            t("No refund")
          )}
        </dd>
        <dt className="text-gray-500">{t("Closed")}</dt>
        <dd className="text-gray-900">{detail.closedAt ? `${detail.channel === "ebay" ? t("by eBay") : t("in Wilma")} · ${when(detail.closedAt)}` : t("Not closed yet")}</dd>
        <dt className="text-gray-500">{t("Part outcome")}</dt>
        <dd className="text-gray-900">
          {inspection ? (
            <>
              <span className={classNames("font-semibold", inspection.outcome === "Scrap" ? "text-red-700" : inspection.outcome === "BackToStock" ? "text-green-700" : "")}>{t(OUTCOME_LABELS[inspection.outcome])}</span>
              {inspection.difference ? ` · ${inspection.difference}` : ""}
              {inspection.by ? ` · ${inspection.by}` : ""}
              {inspection.photos?.length ? ` · ${t("inspection + {{count}} photos", { count: inspection.photos.length })}` : ""}
            </>
          ) : (
            t("Never came back")
          )}
        </dd>
        {cost.length > 0 && (
          <>
            <dt className="text-gray-500">{t("Cost to us")}</dt>
            <dd className="text-gray-900">{cost.join(" + ")}</dd>
          </>
        )}
      </dl>
      <span className="text-gray-500">{detail.channel === "ebay" ? t("Nothing left to do on eBay. The timeline stays for the record; a courier claim can be raised from the order.") : t("Nothing left to do. The timeline stays for the record.")}</span>
    </div>
  );
}
